import { useAdminDetail } from "@/hooks/useAdminDetail";
import { useGetDashboardOverview, useGetAllMerchant } from "../misc/mutations";
import TransactionTable from "../misc/TransactionTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, BarChart3, AlertTriangle, Briefcase, CreditCard, Users, Terminal, Loader, ShieldCheck, Filter, ChevronDown, Search } from "lucide-react";
import { IOverview, Merchant } from "../misc/types";
import { useState, useRef, useEffect } from "react";

const Dashboard = () => {
  const { adminLogin } = useAdminDetail();

  // Filter state management
  const [filters, setFilters] = useState<{ [key: string]: string }>({
    responseCode: "",
    merchantName: "",
    cardtype: "",
    paymentType: "",
    rrn: "",
    stan: "",
    search: "",
    terminalId: "",
    type: "",
    merchantId: "",
    transactionId: "",
    status: "",
    fromDate: "",
    toDate: "",
    limit: "",
  });

  // Merchant dropdown state
  const [isMerchantDropdownOpen, setIsMerchantDropdownOpen] = useState(false);
  const [merchantSearchTerm, setMerchantSearchTerm] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const merchantDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch merchants for dropdown
  const { data: merchantsData } = useGetAllMerchant({ size: 1000, page: 1 });

  // Overview filters - only merchantId and merchantName
  const overviewFilters = {
    merchantId: filters?.merchantId || undefined,
    merchantName: filters?.merchantName || undefined,
    terminalId: filters?.terminalId || undefined,
  };

  const { data, isLoading } = useGetDashboardOverview(overviewFilters);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handle merchant selection
  const handleMerchantSelect = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setFilters((prev) => ({
      ...prev,
      merchantId: merchant.merchantId,
      merchantName: merchant.merchantName
    }));
    setIsMerchantDropdownOpen(false);
    setMerchantSearchTerm("");
  };

  // Filter merchants based on search term
  const filteredMerchants = merchantsData?.merchants?.filter((merchant) =>
    merchant.merchantName?.toLowerCase().includes(merchantSearchTerm.toLowerCase()) ||
    merchant.merchantId?.toLowerCase().includes(merchantSearchTerm.toLowerCase())
  ) || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (merchantDropdownRef.current && !merchantDropdownRef.current.contains(event.target as Node)) {
        setIsMerchantDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <h1 className="text-2xl">Welcome, {adminLogin?.name ?? ""}</h1>

      {/* Overview Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-700">Overview Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="merchantId">
              Merchant ID
            </label>
            <input
              id="merchantId"
              name="merchantId"
              type="text"
              value={filters.merchantId || ""}
              onChange={handleFilterChange}
              className="border rounded px-3 py-2 w-full text-sm"
              placeholder="Enter Merchant ID"
            />
          </div>
          <div className="relative" ref={merchantDropdownRef}>
            <label className="block text-sm font-medium mb-1" htmlFor="merchantName">
              Merchant Name
            </label>
            <div className="relative">
              <input
                id="merchantName"
                name="merchantName"
                type="text"
                value={selectedMerchant ? selectedMerchant.merchantName : filters.merchantName || ""}
                onChange={(e) => {
                  setMerchantSearchTerm(e.target.value);
                  if (!selectedMerchant) {
                    handleFilterChange(e);
                  }
                }}
                onFocus={() => setIsMerchantDropdownOpen(true)}
                className="border rounded px-3 py-2 w-full text-sm pr-10"
                placeholder="Search merchants..."
              />
              <button
                type="button"
                onClick={() => setIsMerchantDropdownOpen(!isMerchantDropdownOpen)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>

            {/* Dropdown */}
            {isMerchantDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={merchantSearchTerm}
                      onChange={(e) => setMerchantSearchTerm(e.target.value)}
                      placeholder="Search merchants..."
                      className="w-full pl-8 pr-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="py-1">
                  {filteredMerchants.length > 0 ? (
                    filteredMerchants.map((merchant) => (
                      <button
                        key={merchant._id}
                        type="button"
                        onClick={() => handleMerchantSelect(merchant)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium">{merchant.merchantName}</div>
                        <div className="text-xs text-gray-500">{merchant.merchantId}</div>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No merchants found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid place-content-center p-4">
          <Loader className="animate animate-spin" />
        </div>
      ) : (
        <DashboardCards data={data as IOverview} />
      )}
      <TransactionTable
        filters={filters}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
};

export function DashboardCards({ data }: { data: IOverview }) {
  const {
    totalTransactionCountOfToday,
    totalUniqueMerchantCount,
    totalTransactonsCount,
    totalMerchantRequestsCount,
    totalTransactionVolumeOfToday,
    totalClientsCount,
    totalTerminalsCount,
    totalApprovedDailyTransactionsCount,
    totalDeclinedDailyTransactionsCount,
    totalDailyApprovedTransactionVolume,
    totalDailyDeclinedTransactionVolume,
    totalFailedAndApprovedTransactionVolume,
  } = data;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 my-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Terminals</CardTitle>
          <Terminal className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalTerminalsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Active payment terminals</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Clients</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalClientsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Registered clients</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalTransactonsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">All-time transactions</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Transactions Today</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalTransactionCountOfToday?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Transactions made today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Volume Today</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold">₦{totalTransactionVolumeOfToday?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Today's transaction volume</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Approved Today</CardTitle>
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalApprovedDailyTransactionsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Approved transactions today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Declined Today</CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalDeclinedDailyTransactionsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Declined transactions today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Approved Volume</CardTitle>
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">₦{totalDailyApprovedTransactionVolume?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Volume of approved today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Declined Volume</CardTitle>
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">₦{totalDailyDeclinedTransactionVolume?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Volume of declined today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Approved + Failed Volume</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">₦{totalFailedAndApprovedTransactionVolume?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Sum of approved & failed</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Unique Merchants</CardTitle>
          <Briefcase className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalUniqueMerchantCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Merchants onboarded</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Merchant Requests</CardTitle>
          <Briefcase className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalMerchantRequestsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">New merchant requests</p>
        </CardContent>
      </Card>
    </div>
  );
}



// Sample data

export default Dashboard;
